<template>
    <Widget :title="$t('components.WebMDDashboard.VisibilityMetricsTitle')" :loading="loading" :report-mode="reportMode">
        <v-row align="center" justify="space-between" style="width: 100%">
            <v-col cols="12" sm="4">
                <span class="d-inline-block mx-4 text-caption ddwhite--text" v-for="(item, index) in Object.values(legend)" :key="index">
                    <span class="circle" :style="{ backgroundColor: item.color }"></span> 
                    <span class="label">{{ item.title }}</span>
                </span>
            </v-col>

            <v-col cols="12" sm="4" class="text-right" v-if="!isReport">
                <span class="d-block input-label mr-6 ddgrey--text text-caption">
                    {{ $t("components.Filters.FilterText") }}
                </span>
                <v-select
                    solo
                    dense
                    class="d-inline-block groupby"
                    v-model="groupBy"
                    item-text="hint"
                    item-value="value"
                    :items="[
                        { value: 'all', hint: $t('components.Filters.AllGroupsLabel') },
                        { value: 'competitor_impressions', hint: $t('components.WebMDDashboard.CompetitorImpressionsLabel') },
                        { value: 'impressions', hint: $t('components.WebMDDashboard.ImpressionsLabel') },
                        { value: 'profile_views', hint: $t('components.WebMDDashboard.ProfileViewsLabel') } ]"
                    :label="$t('components.Filters.FilterText')"
                    @change="filterData(data)"/>
            </v-col>

            <v-col cols="12" sm="4" class="text-right" v-if="!isReport">
                <span class="d-block input-label mr-6 ddgrey--text text-caption">
                    {{ $t("components.Filters.GroupText") }}
                </span>
                <v-select
                    solo
                    dense
                    class="d-inline-block groupby"
                    v-model="filterBy"
                    item-text="hint"
                    item-value="value"
                    :items="[
                        { value: 'month', hint: $t('components.Filters.MonthLabel') },
                        { value: 'month_cumulative', hint: $t('components.Filters.MonthCumulativeLabel') },
                        { value: 'day', hint: $t('components.Filters.DayLabel') } ]"
                    :label="$t('components.Filters.GroupText')"
                    @change="filterData(data)"/>
            </v-col>
        </v-row>
        <StackedBarChart :stats="stats" :styles="styles"/>
    </Widget>
</template>

<script>
/** 
 * VisibilityMetricsChart wraps a StackedBarChart and contains the functions to parse
 * and format data from the API.
 * 
 * It represents the number of chats versus the number of referrals grouped
 * by month, day of the week or hour of the day.
 */

import { DayLabels } from '@/mixins/TimeLabels';

import Widget from '@/components/dashboard/Widget';

import StackedBarChart from '@/components/dashboard/charts/StackedBarChart';

export default {
    name: 'VisibilityMetricsChart',
    data: () => ({ 
        stats: {},
        styles: {
            position: 'relative',
            width: '100%',
            padding: '24px 24px 0',
        },
        groupBy: 'all',
        filterBy: 'month',
        legend: [],
    }),
    props: {
        loading: {
            type: Boolean,
            default: true
        },
        chartData: {
            type: Object,
            required: true
        },
        reportMode: {
            type: String,
            default: null
        },
        appVersion: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        isReport() { return this.reportMode !== null },
        colors() {
            return this.$vuetify.theme.isDark ? 
                this.$vuetify.theme.themes.dark : this.$vuetify.theme.themes.light;
        },
    },
    mounted() {
        this.inflateLegend();
        if (Array.isArray(this.appVersion) && this.appVersion.includes('VisibilityMetrics_filtered_month_cumulative')) this.filterBy = 'month_cumulative';
        this.updateStats(this.chartData);
        console.log("STATS mounted");
    },
    watch: {
        chartData(newData) {
            this.updateStats(newData);
        },
        appVersion(test) {
            if (Array.isArray(test) && test.includes('VisibilityMetrics_filtered_month_cumulative')) this.filterBy = 'month_cumulative';
        },
        groupBy() {
            this.updateStats(this.chartData);
        },
        filterBy() {
            this.updateStats(this.chartData);
        }
    },
    methods: {
        inflateLegend() {
            const $t = this.$t.bind(this);
            this.legend = {
                competitor_impressions: {
                    title: $t("components.WebMDDashboard.CompetitorImpressionsLabel"),
                    color: this.colors.ddgreen
                },
                impressions: { 
                    title: $t("components.WebMDDashboard.ImpressionsLabel"),
                    color: this.colors.dddarkblue
                },
                profile_views: { 
                    title: $t("components.WebMDDashboard.ProfileViewsLabel"),
                    color: "#F6AA53"
                },
            }
        },
        updateStats(data) {
            if (data) {
                const items = this.filterData(data);
                this.stats = this.formatItems(items);
            }
            switch (this.reportMode) {
                case 'weekly':
                    this.filterBy = 'day';
                    break;
                case 'monthly':
                    this.filterBy = 'month';
                    break;
            }
        },
        filterData(data) {
            const $t = this.$t.bind(this);
            const items = data[this.filterBy];

            if (items) {
                let formatedItems = {};
                let labels = [];

                switch (this.filterBy) {
                    case 'month':
                    case 'month_cumulative':
                        labels = Object.keys(items);
                        break;
                    case 'day': 
                        labels = DayLabels.map(l => $t(`components.ChatsStats.${l}`));
                        break;
                }

                const keys = Object.keys(items);
                const { length } = keys;
                for (let i = 0; i < length; i++) {
                    const key = keys[i];
                    const label = labels[i];
                    formatedItems[label] = items[key];
                }
                return formatedItems;
            }
            
            return {};
        },
        formatItems(items) {
            const $t = this.$t.bind(this);

            const labels = [...Object.keys(items)];
            const datasets = [];

            const competitorImpressions = {
                barPercentage: 0.5,
                label: $t("components.WebMDDashboard.CompetitorImpressionsLabel"),
                backgroundColor: this.colors.ddgreen,
                data: []
            }

            const impressions = {
                barPercentage: 0.5,
                label: $t("components.WebMDDashboard.ImpressionsLabel"),
                backgroundColor: this.colors.dddarkblue,
                data: []
            }

            const profileViews = {
                barPercentage: 0.5,
                label: $t("components.WebMDDashboard.ProfileViewsLabel"),
                backgroundColor: "#F6AA53",
                data: []
            }

            const { length } = labels;
            for (let i = 0; i < length; i++) {
                const item = items[labels[i]];
                competitorImpressions.data.push(item.competitor_impressions);
                impressions.data.push(item.impressions);
                profileViews.data.push(item.profile_views);
            }

            const { groupBy } = this;
            switch (groupBy) {
                case 'competitor_impressions':
                    datasets.push(competitorImpressions);
                    break;
                case 'impressions':
                    datasets.push(impressions);
                    break;
                case 'profile_views':
                    datasets.push(profileViews);
                    break;
                case 'all':
                    datasets.push(competitorImpressions);
                    datasets.push(impressions);
                    datasets.push(profileViews);
                    break;
            }

            return { labels, datasets };
        }
    },
    components: { Widget, StackedBarChart }
}
</script>

<style>
    .circle {
        display: inline-block;
        vertical-align: top;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin: 4px 12px;
    }

    .label {font-size:  1.2em !important;}
    .input-label.input-label {font-size:  1.02em !important;}
    
    .groupby .v-text-field__details { display: none; }
</style>